import { useActiveStation } from '@app/core/stations/hooks/useActiveStation';
import { nlpIntegration } from '@mi-front/api-clients';
import { useQueryClient } from '@tanstack/react-query';
import debugFn from 'debug';
import { useEffect } from 'react';

import { PANEL_REFETCH_INTERVAL_MS, TOPICS_LIMIT } from '../consts';

const debug = debugFn('useLatestLiveTopics');

/**
 * Fetches live topics for the active station with polling.
 * Returns slug, topics list, and loading/success state.
 * When new data arrives, invalidates the track's time-range topics query so the track refetches.
 */
export function useLatestLiveTopics() {
  const { slug } = useActiveStation();
  const queryClient = useQueryClient();

  const {
    data: topics = [],
    isSuccess,
    isLoading,
  } = nlpIntegration.useGetLiveTopicsLatestTopics(
    slug ?? '',
    { limit: TOPICS_LIMIT },
    {
      query: {
        enabled: !!slug,
        refetchInterval: PANEL_REFETCH_INTERVAL_MS,
      },
    },
  );

  // Invalidate track's time-range query when latest topics update, so the track refetches and shows new items.
  useEffect(() => {
    if (!topics.length) {
      return;
    }

    debug('Latest topics updated, invalidating track topics query', slug);
    queryClient.invalidateQueries({
      queryKey: nlpIntegration.queryTopicsLiveTopicsQueryKey(slug),
    });
  }, [queryClient, slug, topics]);

  return { slug, topics, isSuccess, isLoading };
}
